"use client"

import { useEffect } from "react"

import { useSofiaPageSummary } from "@/hooks/useSofiaPageSummary"

import { useCovi } from "../contexts/CoviContext"

/**
 * The subject currently presented in the chat, so a page that keeps publishing
 * the same summary does not keep re-presenting it.
 *
 * Deliberately the LAST subject rather than every subject ever seen. Remembering
 * them all reads as "you have been shown this" — which silently made half of
 * navigation dead: stepping forward through runs presented each new one, while
 * stepping back through the very same runs presented nothing, because those ids
 * had been banked on the way out. What matters is whether the page is now about
 * something other than what is on screen, and revisiting a run is exactly that.
 *
 * Module-level rather than mount-scoped so a remount of the app shell does not
 * reopen the summary the user is already looking at.
 */
let presentedSourceId: string | null = null

/**
 * Drop a page's Sofia summary into the chat while the chat is on screen.
 *
 * A summary is Sofia talking about the page, so it is one of her messages —
 * not a card pinned to a corner of the app. Presenting it means opening a
 * thread on it: the summary lands as an ordinary assistant message the user can
 * reply to, and `useSofiaSummaryReveal` plays it in so it reads as something
 * Sofia is saying now rather than history that was already sitting there.
 *
 * A closed panel stays closed. Sofia never opens herself to deliver a summary:
 * forcing the panel open on every arrival meant closing it lasted exactly one
 * navigation — and a page refresh, which resets the module state below, undid
 * even that. Presentation is deferred instead of skipped: the subject is not
 * banked while the panel is closed, so opening Sofia on the page presents the
 * summary at that moment.
 *
 * An OPEN panel showing a conversation the user is in is left alone for the
 * same reason. Presenting means making a new thread the active one, and the
 * panel shows only the active thread — so a summary arriving mid-conversation
 * replaced what the user was reading with a chat they never started. The old
 * thread was never deleted, but "recoverable from the thread list" is not the
 * same as "still on screen", and swapping it out during navigation reads as
 * losing it. Engagement is the test, not emptiness: a thread a page opened is
 * on trial until the user answers it, so consecutive page summaries still
 * replace one another exactly as before.
 *
 * Deferred, again not banked — the summary presents the moment the surface is
 * free, whether that is the user opening the panel, starting a new chat, or
 * coming back to the page later.
 *
 * The thread opens ON TRIAL. Sofia offering an opinion on a page is not the
 * user starting a conversation, so it is kept only if they answer it — see
 * OpenThreadOptions#ephemeral. Otherwise browsing five pages would leave five
 * chats nobody asked for.
 *
 * Presented once per arrival at a subject. Re-publishing the same summary — a
 * refetch, a filter that does not move the window — changes nothing, and neither
 * does closing the panel while staying put. Moving to another run or timeframe,
 * or leaving and coming back, is what brings Sofia out again.
 *
 * Mount this once at the app-shell level (it is in CoviMount) so it is listening
 * whatever route the user lands on.
 */
export function useSofiaPageSummaryPresenter({
  enabled,
}: {
  /** False where the panel cannot show — hidden routes, its own surfaces. */
  enabled: boolean
}): void {
  const summary = useSofiaPageSummary()
  const { discardEphemeralThread, isOpen, threadEngaged } = useCovi()

  useEffect(() => {
    if (!enabled) {
      presentedSourceId = null
      return
    }
    if (!summary) {
      // Nothing on this page to be about, so whatever was presented is behind
      // us — coming back to it later is a fresh arrival, not a repeat.
      presentedSourceId = null
      return
    }
    if (!isOpen) {
      return
    }
    summary.onVisible?.()
    if (!summary.markdown.trim() || threadEngaged) {
      // Defer, don't bank: the summary presents if and when the chat surface is
      // free — the user opens the panel here, or leaves the conversation they
      // are in without leaving the page.
      return
    }
    if (presentedSourceId === summary.sourceId) {
      return
    }
    presentedSourceId = summary.sourceId
    summary.onPresent()
  }, [enabled, isOpen, summary, threadEngaged])

  // The trial thread belongs to the summary that opened it, so it ends when
  // that summary stops being what the page is showing — navigating away, or on
  // to a different run or timeframe. Keyed on the subject rather than the route
  // so a filter change that leaves the summary alone leaves the thread alone
  // too. A no-op once the user has spoken in it: engagement promoted it.
  const sourceId = summary?.sourceId ?? null
  useEffect(
    () => () => discardEphemeralThread(),
    [sourceId, discardEphemeralThread],
  )
}
